import { Avatar, Box, Button, HStack, Spacer, Stack, VStack } from '@chakra-ui/react';
import noop from 'lodash/noop';
import React from 'react';
import { DeviceMode } from 'types';

import { ILocation } from 'models/location';
import { Role } from 'models/user';

import { PrimaryButton, Typography } from 'components/atoms';
import { ColumnsType, Table, TableProps } from 'components/organisms/Table';

import { useDeviceMode, useTranslate } from 'hooks/common';

type Props = {
  locations: ILocation[] | null;
  functionTitle?: string;
  functionAction?: (location: ILocation) => void;
  loading: boolean;
  currentRole: Role;
  creditsLeftToSpend: number | null;
} & Omit<TableProps<ILocation>, 'data' | 'columns'>;

export const LocationCreditList: React.FC<Props> = ({
  loading,
  functionAction = noop,
  functionTitle,
  locations,
  currentRole,
  creditsLeftToSpend,
  ...props
}) => {
  const device = useDeviceMode();
  const t = useTranslate();

  const renderNegativeWarning = () => (
    <Typography.Text color="red.500" fontSize="sm">
      {currentRole === Role.HqManager
        ? t('message.assignCreditsToNegativeLocation')
        : t('message.askHqToAssignCredits')}
    </Typography.Text>
  );

  const columns: ColumnsType<ILocation>[] = [
    {
      key: 'name',
      title: t('label.locationName'),
      dataIndex: 'name',
      allowSort: true,
      sortPath: 'location_name',
      width: '30%',
      render: (value, record, index, { isActive }) => {
        return (
          <HStack spacing="4">
            <Avatar
              bg={isActive ? 'white' : 'tango.300'}
              color={isActive ? 'secondary' : 'white'}
              name={value}
              src={record.url_logo}
            />
            <VStack align="flex-start" spacing="1">
              <Typography.Title>{value}</Typography.Title>
              {Number(record.usable_credits ?? 0) < 0 && renderNegativeWarning()}
            </VStack>
          </HStack>
        );
      },
      renderMobile: (value) => {
        return <Typography.Text>{value}</Typography.Text>;
      },
      pined: true,
    },
    {
      key: 'usable_credits',
      title: t('label.usableCredits'),
      dataIndex: 'usable_credits',
      width: '12%',
      cellStyleProps: {
        textAlign: 'center',
      },
      allowSort: false,
      render: (value) => (
        <Typography.Title size="lg" color={Number(value ?? 0) < 0 ? 'red.500' : undefined}>
          {value?.toLocaleString('en-US') || 0}
        </Typography.Title>
      ),
    },
    {
      key: 'reserved_credits',
      title: t('label.reservedCredits'),
      dataIndex: 'reserved_credits',
      width: '12%',
      cellStyleProps: {
        textAlign: 'center',
      },
      allowSort: false,
      render: (value) => (
        <Typography.Title size="lg">{Number(value ?? 0).toLocaleString('en-US')}</Typography.Title>
      ),
    },
    {
      key: 'runway_days',
      title: t('label.estimatedDaysBeforeNextTopUp'),
      dataIndex: 'runway_days',
      width: '22%',
      cellStyleProps: {
        textAlign: 'center',
      },
      allowSort: false,
      render: (value) => (
        <Typography.Title size="lg">{value ?? t('label.notAvailable')}</Typography.Title>
      ),
    },
    {
      key: 'consumed_credits',
      title: `${t('label.consumedCredits')} ${t('label.currentMonth')}`,
      dataIndex: 'consumed_credits',
      width: '12%',
      cellStyleProps: {
        textAlign: 'center',
      },
      allowSort: true,
      render: (value, record) => (
        <Typography.Title size="lg">
          {Number(
            record.current_month_consumed_credit ?? record?.consumed_credits ?? 0,
          ).toLocaleString('en-US')}
        </Typography.Title>
      ),
    },
    {
      key: 'action',
      title: t('label.function'),
      shouldShowTitleInMobile: false,
      width: '12%',
      render(value, record) {
        return (
          <Button variant="primary" w="100%" onClick={() => functionAction(record)}>
            {functionTitle}
          </Button>
        );
      },
    },
  ];

  const columnsMobile: ColumnsType<ILocation>[] = [
    {
      key: 'name',
      title: t('label.locationName'),
      dataIndex: 'name',
      allowSort: true,
      shouldShowTitleInMobile: false,
      render: (value, record, index, { isActive }) => {
        return (
          <Stack spacing="4">
            <HStack pl="20px">
              <Avatar
                bg={isActive ? 'white' : 'tango.300'}
                color={isActive ? 'secondary' : 'white'}
                name={value}
                src={record.url_logo}
                size="sm"
              />
              <VStack align="flex-start" spacing="1">
                <Typography.Title fontSize="md">{value}</Typography.Title>
                {Number(record.usable_credits ?? 0) < 0 && renderNegativeWarning()}
              </VStack>
            </HStack>
            <HStack>
              <Spacer />
              <VStack textAlign="center" spacing="0">
                <Typography.Title
                  fontSize="md"
                  lineHeight="1"
                  color={Number(record.usable_credits ?? 0) < 0 ? 'red.500' : undefined}
                >
                  {Number(record?.usable_credits ?? 0).toLocaleString('en-US')}
                </Typography.Title>
                <Typography.Text>{t('label.usableCredits')}</Typography.Text>
              </VStack>
              <VStack textAlign="center" spacing="0">
                <Typography.Title fontSize="md" lineHeight="1">
                  {Number(record?.reserved_credits ?? 0).toLocaleString('en-US')}
                </Typography.Title>
                <Typography.Text>{t('label.reservedCredits')}</Typography.Text>
              </VStack>
              <VStack textAlign="center" spacing="0">
                <Typography.Title fontSize="md" lineHeight="1">
                  {record?.runway_days ?? t('label.notAvailable')}
                </Typography.Title>
                <Typography.Text>{t('label.estimatedDaysBeforeNextTopUp')}</Typography.Text>
              </VStack>
              <VStack textAlign="center" spacing="0">
                <Typography.Title fontSize="md" lineHeight="1">
                  {Number(
                    record?.current_month_consumed_credit ?? record?.consumed_credits ?? 0,
                  ).toLocaleString('en-US')}
                </Typography.Title>
                <Typography.Text>
                  {t('label.consumedCredits')} ({t('label.currentMonth')})
                </Typography.Text>
              </VStack>
              <Spacer />
              <Box>
                <PrimaryButton onClick={() => functionAction(record)} h={8}>
                  {functionTitle}
                </PrimaryButton>
              </Box>
            </HStack>
          </Stack>
        );
      },
    },
  ];

  return (
    <Table
      loading={loading}
      columns={device !== DeviceMode.Mobile ? columns : columnsMobile}
      data={locations}
      rowKey={(record) => record.id}
      rowStyleProps={(record) => (record.status === 0 ? { className: `disable` } : undefined)}
      collapsible={false}
      cardStyles={{
        p: { base: '10px', lg: '15px 30px' },
        boxShadow: '0 3px 6px #f0761533',
      }}
      maxHeight="max(50vh, 500px)"
      {...props}
    />
  );
};
